/**
 * Calculate time range based on duration bucket configuration.
 *
 * @param {Object} durationBucket - Configuration object with min, max (optional), and unit keys
 * @param {number} durationBucket.min - Minimum duration value
 * @param {number} [durationBucket.max] - Maximum duration value (optional)
 * @param {string} durationBucket.unit - Time unit ('hour' or 'days')
 * @returns {Object} Object containing startTime and endTime as ISO strings
 *
 * @example
 * // Returns time range for last 1-7 days
 * calculateTimeRange({ min: 1, max: 7, unit: "days" })
 * // => { startTime: "2026-01-26T...", endTime: "2026-02-01T..." }
 */
export const calculateTimeRange = (durationBucket) => {
  if (!durationBucket || typeof durationBucket !== 'object') {
    return { startTime: null, endTime: null };
  }

  const now = new Date();
  const unit = durationBucket.unit || 'days';
  const minValue = durationBucket.min || 0;
  const maxValue = durationBucket.max;

  // Calculate end time (more recent)
  const endTime = new Date(now);
  if (unit === 'hour') {
    endTime.setHours(endTime.getHours() - minValue);
  } else if (unit === 'days') {
    endTime.setDate(endTime.getDate() - minValue);
  } else {
    // Default to days if unit is unknown
    endTime.setDate(endTime.getDate() - minValue);
  }

  // Calculate start time (older)
  let startTime = null;
  if (maxValue !== null && maxValue !== undefined) {
    startTime = new Date(now);
    if (unit === 'hour') {
      startTime.setHours(startTime.getHours() - maxValue);
    } else if (unit === 'days') {
      startTime.setDate(startTime.getDate() - maxValue);
    } else {
      startTime.setDate(startTime.getDate() - maxValue);
    }
    startTime = startTime.toISOString();
  }
  // If no max value, startTime stays null (for 30+ days old scenario)

  return {
    startTime: startTime,
    endTime: endTime.toISOString(),
  };
};

/**
 * Extract duration bucket from filter value and calculate time range
 *
 * @param {string|Object} filterValue - The filter value, either JSON string or object
 * @returns {Object|null} Time range object or null if invalid
 */
export const extractTimeRangeFromFilter = (filterValue) => {
  try {
    let durationBucket;

    if (typeof filterValue === 'string') {
      durationBucket = JSON.parse(filterValue);
    } else if (typeof filterValue === 'object') {
      durationBucket = filterValue;
    } else {
      return null;
    }

    // Check if it's a valid duration bucket
    if (durationBucket && (durationBucket.min !== undefined || durationBucket.max !== undefined)) {
      return calculateTimeRange(durationBucket);
    }

    return null;
  } catch (error) {
    console.warn('Error extracting time range from filter:', error);
    return null;
  }
};
